import { mockFormData } from '../../mocks/mock-form';
import { mockEntityFormData } from '../../mocks/mock-entity-form';
import { mockCalendarEventFormData } from '../../mocks/mock-calendar-form';
import { mockTableFieldFormData } from '../../mocks/mock-table-field';
import { mockFormPostResponsesByUrl } from '../../mocks/mock-form-post-response';

/**
 * Mock live search (search POST, entity panel URLs) and form GET/POST when configured.
 * Set to `true` locally to demo without backend support; keep `false` in commits.
 */
export const USE_LIVE_SEARCH_MOCKS = false;

/** When true, form GET config is served from local mocks ({@link getFormMockConfig}). */
export const USE_FORM_MOCKS = USE_LIVE_SEARCH_MOCKS;

const MOCK_FORM_LOAD_DELAY_MS = 1000;
const MOCK_FORM_POST_DELAY_MS = 400;

/** @type {Record<string, ApiFormResponse>} */
const MOCK_FORM_CONFIG_BY_KEY = {
  form: mockFormData,
  entity: mockEntityFormData,
  calendar: mockCalendarEventFormData,
  mergeTable: mockTableFieldFormData,
};

/** Panel URLs used when {@link USE_LIVE_SEARCH_MOCKS} is true (load {@link mockEntityFormData}). */
export const ENTITY_FORM_MOCK_URL_ADD = '/form/contact/new';
export const ENTITY_FORM_MOCK_URL_EDIT = '/form/contact/42/edit';

/**
 * URL for opening the entity form panel when mocks are enabled.
 * @param {boolean} hasEntity - Whether the parent field already has a selected entity
 * @returns {string}
 */
export function getEntityFormPanelUrl(hasEntity) {
  return hasEntity ? ENTITY_FORM_MOCK_URL_EDIT : ENTITY_FORM_MOCK_URL_ADD;
}

/**
 * Build entity edit form URL by appending the entity id to the base URL.
 * @param {string} baseFormUrl
 * @param {string} entityId
 * @returns {string}
 */
export function buildEntityEditFormUrl(baseFormUrl, entityId) {
  return `${baseFormUrl}&id=${encodeURIComponent(entityId)}`;
}

/**
 * Resolve which form URL to open from an EntityField (mock panel URLs vs API base URL).
 * @param {boolean} hasEntity
 * @param {string} baseFormUrl
 * @param {string} [entityId]
 * @returns {string}
 */
export function resolveEntityPanelFormUrl(hasEntity, baseFormUrl, entityId = '') {
  if (USE_LIVE_SEARCH_MOCKS) {
    return getEntityFormPanelUrl(hasEntity);
  }
  if (hasEntity && entityId) {
    return buildEntityEditFormUrl(baseFormUrl, entityId);
  }
  return baseFormUrl;
}

/**
 * @param {string} formUrl
 * @returns {boolean}
 */
export function isEntityFormUrl(formUrl) {
  if (!formUrl) return false;
  if (formUrl === 'entity') return true;
  return typeof formUrl === 'string' && formUrl.includes('/form/contact');
}

/**
 * Whether GET form config should be served from mocks (entity panel only).
 * @param {string} formUrl
 * @returns {boolean}
 */
export function shouldUseEntityFormMock(formUrl) {
  return USE_FORM_MOCKS && isEntityFormUrl(formUrl);
}

/**
 * Whether GET form config should be served from mocks (all forms, including Table panel).
 * @param {string} formUrl
 * @returns {boolean}
 */
export function shouldUseFormMock(formUrl) {
  return USE_FORM_MOCKS && Boolean(formUrl);
}

/**
 * Map a form URL or dev shorthand to a mock dataset key (legacy manager switch).
 * @param {string} formUrl
 * @returns {'form' | 'entity' | 'calendar' | 'mergeTable'}
 */
function getFormMockLoaderKey(formUrl) {
  if (formUrl === 'calendar') return 'calendar';
  if (formUrl === 'mergeTable') return 'mergeTable';
  if (isEntityFormUrl(formUrl)) return 'entity';
  return 'form';
}

/**
 * @param {number} ms
 * @returns {Promise<void>}
 */
function delay(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Load form configuration from mocks (same delay as former managerService).
 * @param {string} formUrl - Form URL from API or dev shorthand (e.g. "form", "calendar")
 * @returns {Promise<ApiFormResponse>}
 */
export async function getFormMockConfig(formUrl) {
  await delay(MOCK_FORM_LOAD_DELAY_MS);
  const key = getFormMockLoaderKey(formUrl);
  return MOCK_FORM_CONFIG_BY_KEY[key];
}

/**
 * Load POST response from mocks when configured for the submit URL.
 * @param {string} sendFormUrl
 * @returns {Promise<ApiFormPostResponse | null>} Mock response, or null if URL is not mocked
 */
export async function getFormPostMockResponse(sendFormUrl) {
  const mockResponse = mockFormPostResponsesByUrl[sendFormUrl];
  if (!mockResponse) {
    return null;
  }
  await delay(MOCK_FORM_POST_DELAY_MS);
  return mockResponse;
}
